import { Button, Dropdown, Space, Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdKeyboardArrowDown, MdOutlineAdd } from 'react-icons/md'

import { GENERATE_DOCUMENT_TEXTS as T } from './GenerateDocument'
import { GenerateDocumentDrawer } from './GenerateDocumentDrawer'
import { GenerateDocumentStore } from './Stores/GenerateDocumentStore'

import type { CrmDocumentGenerationConfig } from './GenerateDocument'
import type { MenuProps } from 'antd'

export interface GenerateDocumentMenuProps {
    config: CrmDocumentGenerationConfig
    /** Поле власника в документі: `client`, `carrier` */
    ownerField: string
    ownerUuid: string
    /** Номер документа за замовчуванням (заявка — її Ід) */
    defaultNumber?: string
    /** Ручне створення документа — картка з формою */
    onCreate: () => void
    /** Перечитати список вкладки після генерації */
    onGenerated: () => void
}

/**
 * Кнопка «Додати документ» вкладки «Друковані форми» з меню «Генерація
 * документу» (`SPEC-012` §9.2; легасі `AddButtonClientDocumentCrud.jsx:117-139`).
 *
 * Клік по кнопці — ручне створення документа, стрілка поруч — типи документа
 * для генерації. Без жодного типу стрілки немає, а ручне створення лишається
 * (N-10: у легасі зникала вся кнопка). Хто бачить кнопку — вирішує вкладка
 * (`_permissions.update` власника, P-10).
 *
 * ⚠️ `Dropdown.Button` в antd 6 застарів — кнопка й стрілка зібрані з
 * `Space.Compact`, як радить antd.
 */
export const GenerateDocumentMenu = observer(
    ({ config, ownerField, ownerUuid, defaultNumber, onCreate, onGenerated }: GenerateDocumentMenuProps) => {
        const [store] = useState(() => new GenerateDocumentStore(config, ownerField))

        useEffect(() => {
            void store.loadTypes()
        }, [store])

        const items: MenuProps['items'] =
            store.types.length > 0
                ? [
                      {
                          type: 'group',
                          key: 'generate',
                          label: T.menuGroup,
                          children: store.types.map((type) => ({ key: type.uuid, label: type.title })),
                      },
                  ]
                : []

        return (
            <>
                <Space.Compact>
                    <Button type="primary" icon={<MdOutlineAdd />} onClick={onCreate}>
                        {T.add}
                    </Button>
                    {items.length > 0 ? (
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            menu={{
                                items,
                                onClick: ({ key }) => store.open(String(key), ownerUuid, onGenerated, defaultNumber),
                            }}
                        >
                            <Tooltip title={T.moreLabel}>
                                <Button type="primary" icon={<MdKeyboardArrowDown />} aria-label={T.moreLabel} />
                            </Tooltip>
                        </Dropdown>
                    ) : null}
                </Space.Compact>

                <GenerateDocumentDrawer store={store} />
            </>
        )
    }
)
